/**
 * What goes on the preview stage, and how it is framed.
 *
 * `PreviewPane` owns the mat, the bar, the zoom and the five states; it is content-agnostic and takes
 * the artifact as `children` **authored in the artifact's own coordinate space** — a slide at
 * 1920x1080, an image at its pixel size — which the frame then scales. Nothing in v1.5 renders an
 * artifact, so this file supplies the mechanism.
 *
 * The four renderers live beside this file, one per kind, and every one of them is the canvas's own:
 * `ArtifactDocument` is `DocSlideFrame`'s two surfaces without the canvas state, `DocumentBody` is the
 * canvas's Tiptap prose, `WebsiteBody` is `BirtvyWebCard`'s body dispatch, and `ImageBody` is an
 * `<img>` at the size `measureImage` reports. Nothing here invents a way to draw something the app can
 * already draw.
 *
 * A fifth renderer answers a different question. `WebsiteIntake` is not a way of drawing a website —
 * it is what a website card with no address yet shows instead of the pane's generic empty board, and
 * it travels as `emptyOverride` rather than as `children` for that reason: `children` is the artifact,
 * and there is no artifact here yet.
 */
import type { ReactNode } from 'react';
import { useSyncExternalStore } from 'react';
import {
  SOCIAL_CARD_SIZE,
  WEB_CARD_DEFAULT_SIZE,
} from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import { DOC_H, DOC_W } from '~/design-system/components/DeckGen/birtvy/birtvyDoc';
import type { ArtifactKind, PreviewStatus } from '~/design-system/components/v1_5/Preview';
import type { DeckEntity } from '../deckContent';
import { ArtifactDocument } from './ArtifactDocument';
import { DocumentEditor } from './DocumentEditor';
import { ImageBody } from './ImageBody';
import { StageBoundary } from './StageBoundary';
import { WebsiteBody } from './WebsiteBody';
import { WebsiteIntake } from './WebsiteIntake';
import { resolveWebIntake } from '../directory/useWebActions';
import {
  clearStageFailed,
  entityKey,
  markStageFailed,
  measuredImageSize,
  stageFailed,
  stageServerVersion,
  stageVersion,
  subscribeStage,
} from './stageSignals';

/** A slide's own coordinate space. Birtvy authors artifacts at 1920x1080 and they never scroll. */
export const ARTIFACT_W = 1920;
export const ARTIFACT_H = 1080;

/** The subset of `PreviewPaneProps` that depends on WHAT is open. Spread straight into the pane. */
export interface StageProps {
  kind: ArtifactKind;
  naturalWidth: number;
  naturalHeight: number;
  /** Website kind only: drives `BrowserBar`. */
  url?: string;
  live?: boolean;
  status: PreviewStatus;
  children: ReactNode;
  /**
   * What to draw INSTEAD of the pane's "Nothing open" board, when this item's empty state is a
   * question rather than an absence. One item has one so far: a website card with no address yet,
   * whose empty state is the field that asks for it.
   *
   * It is supplied from here rather than composed by `DeckWorkspace` for the reason the whole file
   * exists — this is where the deck decides how a kind is framed, and the pane is content-agnostic
   * by design. `PreviewPane` renders whatever node it is handed and never learns what a website is.
   */
  emptyOverride?: ReactNode;
  /**
   * The failed board's two actions, which are facts about the open ITEM and so belong with the rest of
   * them. Both are additive and optional: a host that spreads these props gets them wired for free, and
   * a host that does not is exactly where it was.
   */
  onRetry?: () => void;
  onViewSource?: () => void;
}

/**
 * Nothing selected, framed as whatever the directory is currently showing.
 *
 * `emptyKind` only reaches the board's copy: `PreviewPane`'s `framed`/`bleeds` both require an
 * artifact, so on an empty stage the kind changes no geometry — which is why the artifact's natural
 * size stays here for every kind rather than being faked per kind. Defaults to `slide`, the answer
 * for the two views that are not a kind (`all`, `other`) as well as for artifacts.
 */
function nothingOpen(emptyKind: ArtifactKind = 'slide'): StageProps {
  return {
    kind: emptyKind,
    naturalWidth: ARTIFACT_W,
    naturalHeight: ARTIFACT_H,
    status: 'empty',
    children: null,
  };
}

/**
 * Open a source in a new tab, as TEXT.
 *
 * The failed board says "The content is safe. This is a render problem, not a data one", and the action
 * under it has to prove that — so an artifact whose HTML will not render opens as its HTML, not as
 * another attempt to render it. A blob URL rather than a data URL because sources run 10-30KB and some
 * of them a great deal more. Revoked on a long timer: the new tab has to finish fetching it first, and
 * a few dozen KB held for a minute is cheaper than a tab that opens blank.
 */
function viewText(text: string): void {
  if (typeof window === 'undefined') return;
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function viewUrl(url: string): void {
  if (typeof window === 'undefined' || !url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * The framing decides only the SHAPE: a slide and a website are white cards floating on the mat, an
 * image is the same card with no ground of its own, and a document is not a card at all — it replaces
 * the mat with its own ground. Everything below the frame is the artifact's own content in the
 * artifact's own palette.
 */
export function stageFor(entity: DeckEntity | undefined, emptyKind?: ArtifactKind): StageProps {
  if (!entity) return nothingOpen(emptyKind);

  const key = entityKey(entity);
  const failed = stageFailed(key);
  const onRetry = () => clearStageFailed(key);
  const onError = () => markStageFailed(key);

  switch (entity.kind) {
    case 'documents': {
      const { doc } = entity;
      return {
        kind: 'document',
        // A document is never fitted — `DocumentSurface` replaces the mat and scrolls — so these two
        // are reported rather than used. The canvas page's real numbers, so nothing downstream has to
        // divide by a zero it was handed for want of an answer.
        naturalWidth: DOC_W,
        naturalHeight: DOC_H,
        status: failed ? 'failed' : 'ready',
        onRetry,
        onViewSource: () => viewText(doc.content),
        children: failed ? null : (
          <StageBoundary resetKey={`${key}:${doc.content.length}`} onError={onError}>
            {/* The EDITOR, not the read-only body (Guri, 2026-08-30: the real Tiptap, editable).
                Keyed on the card so switching documents remounts a clean instance; the editor's
                own unmount flush keeps the outgoing card's last keystrokes safe. It saves through
                docEditBus — a workspace that registers no bus gets a read-only editor, so this
                stays a pure function of the entity. */}
            <DocumentEditor key={doc.id} doc={doc} />
          </StageBoundary>
        ),
      };
    }

    case 'images': {
      const { image } = entity;
      /**
       * The bitmap's real size, measured — not `image.width/height`, which is the canvas INSERT box
       * (aspect preserved, longest side clamped to 640), and not a guessed 1920x1080. Until the measure
       * lands the insert box stands in: it carries the true aspect, so the fit is already right and
       * only the number is provisional. See `stageSignals`.
       */
      const measured = measuredImageSize(image.src);
      const broken = failed || !image.src;
      return {
        kind: 'image',
        naturalWidth: measured?.width ?? image.width,
        naturalHeight: measured?.height ?? image.height,
        status: broken ? 'failed' : 'ready',
        onRetry,
        onViewSource: () => viewUrl(image.src),
        children: broken ? null : (
          <StageBoundary resetKey={`${key}:${image.src}`} onError={onError}>
            <ImageBody image={image} itemKey={key} />
          </StageBoundary>
        ),
      };
    }

    case 'websites': {
      const { web } = entity;
      /**
       * The honest box for a web card, which is NOT 1440x900: Birtvy's own geometry is 760x540 for a
       * site (`WEB_CARD_DEFAULT_SIZE`) and 460x600 for a post (`SOCIAL_CARD_SIZE`) — a post is laid out
       * portrait, like an embed, rather than as a wide capture. The card's OWN width and height win
       * when it has them, which is the rule the fullscreen viewer already follows so that the body
       * reflows identically in both: a social card auto-sizes to its content, and a card the user
       * resized is that shape now.
       */
      const fallback = web.post ? SOCIAL_CARD_SIZE : WEB_CARD_DEFAULT_SIZE;
      const sized = web.width > 0 && web.height > 0;
      /** Something to draw: the post, the live page, or the capture. Anything else is not a preview. */
      const showable = !!web.post || (web.status === 'ready' && (web.frameable || !!web.screenshotUrl));
      const status: PreviewStatus =
        failed || web.status === 'error' ? 'failed'
        : showable ? 'ready'
        : web.status === 'loading' ? 'loading'
        : web.status === 'empty' ? 'empty'
        : 'failed';
      return {
        kind: 'website',
        naturalWidth: sized ? web.width : fallback.width,
        naturalHeight: sized ? web.height : fallback.height,
        url: web.url,
        // The chrome's live dot answers "is this the page or a picture of it", which is a different
        // question from "did the scrape work". Only a frameable card is showing the page itself.
        live: web.frameable === true,
        status,
        onRetry,
        onViewSource: () => viewUrl(web.url),
        /**
         * A card with no address yet asks for one, right here, instead of the pane's "Nothing open".
         * That board would be wrong twice over: something IS open, and the thing it needs is one
         * field long.
         *
         * `key` on the element, so switching between two blank cards remounts the field rather than
         * carrying the first card's half-typed URL onto the second — and re-runs its autofocus.
         *
         * The submit goes through `resolveWebIntake` because `stageFor` is a pure function of the
         * entity and holds no `mutate`; see the seam's own note in `useWebActions`.
         */
        emptyOverride:
          status === 'empty' ? (
            <WebsiteIntake key={key} onSubmit={(url) => resolveWebIntake(web, url)} />
          ) : undefined,
        children: status === 'ready' ? (
          <StageBoundary resetKey={`${key}:${web.screenshotUrl ?? web.url}`} onError={onError}>
            <WebsiteBody web={web} itemKey={key} />
          </StageBoundary>
        ) : null,
      };
    }

    default: {
      const { slide } = entity;
      /**
       * `writing` outranks `empty`. A generating slide has no `docHtml` yet by definition, and the
       * whole point of that board is that you are watching the artifact arrive — an empty state with
       * "Pick an artifact on the left" would be the pane telling you to do the thing you just did.
       */
      const status: PreviewStatus =
        failed ? 'failed'
        : slide.status === 'generating' ? 'writing'
        // An artifact is a slide with `docHtml` set. Without it there is nothing to put on the stage,
        // and `empty` is the honest answer rather than a blank white card pretending to be one.
        : slide.docHtml ? 'ready'
        : 'empty';
      return {
        kind: 'slide',
        naturalWidth: ARTIFACT_W,
        naturalHeight: ARTIFACT_H,
        status,
        onRetry,
        onViewSource: () => viewText(slide.docHtml ?? ''),
        children:
          slide.docHtml && (status === 'ready' || status === 'writing') ? (
            <StageBoundary resetKey={`${key}:${slide.docRev ?? 0}:${slide.docHtml.length}`} onError={onError}>
              <ArtifactDocument
                html={slide.docHtml}
                baked={slide.docBaked}
                title={slide.docMeta?.title ?? `Artifact ${slide.id}`}
              />
            </StageBoundary>
          ) : null,
      };
    }
  }
}

/**
 * `stageFor`, subscribed to the two things it cannot know synchronously.
 *
 * A measured image size and a caught render error both arrive after the props that needed them were
 * computed (see `stageSignals`). `stageFor` reads both from the store, so a plain caller picks them up
 * on its next render for any other reason — correct, but late enough that a broken artifact can sit
 * there as a blank card. This is the same function with a subscription in front of it, so the pane
 * reaches its own `failed` board the moment a renderer throws, and reports the bitmap's real size the
 * moment it is known.
 *
 * A drop-in for `stageFor` at any call site that is already a component render.
 */
export function useStageFor(entity: DeckEntity | undefined, emptyKind?: ArtifactKind): StageProps {
  useSyncExternalStore(subscribeStage, stageVersion, stageServerVersion);
  return stageFor(entity, emptyKind);
}
